import { DATE_MASK_FORMAT, maskWholeNumber, unmaskNumber } from '@givebutter/util';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faHashtag, faPlusMinus } from '@fortawesome/pro-regular-svg-icons';
import {
    faCalendar,
    faCheckSquare,
    faFontCase,
    faPaperclip,
} from '@fortawesome/pro-solid-svg-icons';
import { CustomFieldTypeFriendly } from './types';
import { UploadFile } from 'components/upload';
import { formatDate } from 'helpers/dates';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { CustomField, CustomFieldRequestValue } from 'types/domain';
import { CustomFieldType } from 'types/filter';

type CustomFieldFormValue =
    | string
    | string[]
    | boolean
    | null
    | { start: string | null; end: string | null }
    | UploadFile[];

const toFriendlyType = (type: CustomFieldType): CustomFieldTypeFriendly => {
    switch (type) {
        case 'checkbox':
            return 'checkbox';
        case 'date':
        case 'daterange':
            return 'date';
        case 'multiselect':
        case 'select':
        case 'radio':
            return 'option';
        case 'number':
        case 'percentage':
            return 'number';
        case 'textarea':
        case 'text':
            return 'text';
        case 'file':
        default:
            return 'file';
    }
};

const typeIcon = (type: CustomFieldType): IconDefinition => {
    const typeFriendly = toFriendlyType(type);

    switch (typeFriendly) {
        case 'checkbox':
            return faCheckSquare;
        case 'date':
            return faCalendar;
        case 'option':
            return faPlusMinus;
        case 'number':
            return faHashtag;
        case 'text':
            return faFontCase;
        case 'file':
        default:
            return faPaperclip;
    }
};

const customFieldTypeToReadable = (type: CustomFieldType): string =>
    capitalize(toFriendlyType(type));

const toFormDate = (date: string = DateTime.now().toFormat(DATE_MASK_FORMAT)) =>
    DateTime.fromFormat(date, DATE_MASK_FORMAT, { zone: 'UTC' });

const toFormValue = (type: CustomFieldType, value: any) => {
    switch (type) {
        case 'date':
            return value ? formatDate(toFormDate(value), DATE_MASK_FORMAT) : undefined;
        case 'daterange':
            return {
                start: value?.[0] ? formatDate(toFormDate(value[0]), DATE_MASK_FORMAT) : undefined,
                end: value?.[1] ? formatDate(toFormDate(value[1]), DATE_MASK_FORMAT) : undefined,
            };
        case 'multiselect':
            return value ?? [];
        case 'number':
        case 'percentage':
            return value ? maskWholeNumber(value) : '';
        default:
            return value ?? '';
    }
};

const hasResponse = (customField: CustomField) => customField.response != null;

const toFormValues = (customFields: CustomField[]) => {
    return customFields.reduce(
        (carry, field) => {
            carry[`field_${field.id}`] = toFormValue(
                field.type,
                hasResponse(field) ? field.response?.value : field.default_value,
            );

            return carry;
        },
        {} as { [field: string]: CustomFieldFormValue },
    );
};

const castFormValue = (field: CustomField, value: CustomFieldFormValue) => {
    switch (field.type) {
        case 'checkbox':
            return Boolean(value);
        case 'date':
            return (value as string) || null;
        case 'daterange':
            return isValidDateRange(value) ? value : null;
        case 'number':
        case 'percentage':
            return isValidNumber(value) ? unmaskNumber(value) : null;
        case 'file':
            if (typeof value === 'string') return value;

            if (isValidFileValue(value)) {
                const f = value[0];
                return {
                    mime_type: f.file.type,
                    file_name: f.filename,
                    url: f.getMetadata('url'),
                };
            }

            return null;
        default:
            return value ? (value as string | string[]) : null;
    }
};

const isValidFileValue = (value: CustomFieldFormValue): value is UploadFile[] => {
    return Array.isArray(value) && value.length > 0;
};

const isValidNumber = (value: CustomFieldFormValue): value is string => {
    return typeof value === 'string' && value !== '';
};

const isValidDateRange = (value: CustomFieldFormValue): value is { start: string; end: string } => {
    return (
        value != null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.hasOwn(value, 'start') &&
        Object.hasOwn(value, 'end') &&
        !!value.start &&
        !!value.end
    );
};

const toRequestValues = (
    responses: { [field: string]: CustomFieldFormValue },
    customFields: CustomField[],
    options: { useId: boolean } = { useId: false },
) => {
    const { useId } = options;

    return Object.keys(responses).reduce(
        (carry, key) => {
            const id = Number(key.split('_').pop());
            const nextKey = useId ? id : key;
            carry[nextKey] = castFormValue(
                customFields.find(field => field.id === id)!,
                responses[key],
            );

            return carry;
        },
        {} as { [field: string]: CustomFieldRequestValue },
    );
};

const createFields = (customFields: CustomField[]) => {
    return customFields.filter(field => field.metadata?.show_create);
};

const getReadableDisplayValueByType = (field: CustomField | undefined) => {
    const valueFriendlyResponse = field?.value_friendly ?? field?.response?.value_friendly;
    const defaultValue = field?.default_value;

    if (field?.type === 'checkbox') {
        const checkBoxResponse = field?.response ? field?.response?.value : defaultValue;
        return checkBoxResponse ? 'Yes' : 'No';
    }

    return valueFriendlyResponse || defaultValue;
};

export type { CustomFieldFormValue };
export {
    toFriendlyType,
    typeIcon,
    customFieldTypeToReadable,
    toFormValue,
    createFields,
    toFormValues,
    toRequestValues,
    getReadableDisplayValueByType,
};
